import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Building2, ArrowLeft, FileText, CheckCircle, XCircle, Clock, AlertCircle, ChevronDown, ChevronUp, Search, MessageSquare, Send, Download, Edit, Receipt } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { prestacoesApi, documentosApi, filesApi } from '../api/backend';
import { useAuthStore } from '../store/authStore';
import type { PrestacaoContas, DocumentoFiscal, Comentario, DocumentoFiscalStatus } from '../types';
import { formatCurrencyWithSymbol } from '../helpers/format-money';

const statusOptions: Array<{label: string, value: DocumentoFiscalStatus}> = [
  {
    label: "Pendentes",
    value: 'pendente'
  },
  {
    label: "Deferidos",
    value: 'deferido'
  },
  {
    label: "Indeferidos",
    value: 'indeferido'
  }
]

const getStatusBorderClass = (status: 'pendente' | 'deferido' | 'indeferido') => {
  switch (status) {
    case 'pendente':
      return 'border-yellow-400 dark:border-yellow-500';
    case 'deferido':
      return 'border-green-500 dark:border-green-500';
    case 'indeferido':
      return 'border-red-500 dark:border-red-500';
    default:
      return 'border-gray-200 dark:border-gray-700';
  }
};

const getDocumentButtonClass = (status: 'pendente' | 'deferido' | 'indeferido') => {
   switch (status) {
    case 'pendente':
      return 'bg-yellow-600 text-white disabled:bg-transparent disabled:hover:bg-yellow-100 disabled:border disabled:border-yellow-600 disabled:text-yellow-600 disabled:opacity-60'
    case 'deferido':
      return 'bg-green-600 text-white disabled:bg-transparent disabled:hover:bg-green-100 disabled:border disabled:border-green-600 disabled:text-green-600 disabled:opacity-60'
    case 'indeferido':
      return 'bg-red-600 text-white disabled:bg-transparent disabled:hover:bg-red-100 disabled:border disabled:border-red-600 disabled:text-red-600 disabled:opacity-60'
    default:
      return 'bg-gray-600 text-white disabled:bg-transparent disabled:hover:bg-gray-100 disabled:border disabled:border-gray-600 disabled:text-gray-600 disabled:opacity-60';
  }
}

const handleDownloadComprovante = async (comprovantePath: string) => {
  try {
    const url = await filesApi.getDownloadUrl(comprovantePath);
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error downloading comprovante:', error);
    alert('Erro ao baixar comprovante');
  }
};

export function PrestacaoDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const [prestacao, setPrestacao] = useState<PrestacaoContas | null>(null);
  const [documentos, setDocumentos] = useState<DocumentoFiscal[]>([]);
  const [comentarios, setComentarios] = useState<Comentario[]>([]);
  const [loading, setLoading] = useState(true);
  const [validandoDoc, setValidandoDoc] = useState<number | null>(null);
  const [motivoExpandido, setMotivoExpandido] = useState<number | null>(null);
  const [comentarioExpandido, setComentarioExpandido] = useState<number | null>(null);
  const [filtroBusca, setFiltroBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<DocumentoFiscalStatus>(null);
  const [novoComentario, setNovoComentario] = useState<{[key: number]: string}>({});
  const [enviandoComentario, setEnviandoComentario] = useState<number | null>(null);
  const [novoComentarioGeral, setNovoComentarioGeral] = useState('');
  const [enviandoComentarioGeral, setEnviandoComentarioGeral] = useState(false);
  const [mostrarComentarios, setMostrarComentarios] = useState(false);
  const [modalIndeferimento, setModalIndeferimento] = useState<{docId: number} | null>(null);
  const [motivoIndeferimento, setMotivoIndeferimento] = useState('');
  const [valorGlosa, setValorGlosa] = useState('');
  const [notificacaoMensagem, setNotificacaoMensagem] = useState('');

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prestacaoData, documentosData, comentariosData] = await Promise.all([
        prestacoesApi.get(Number(id)),
        prestacoesApi.getDocumentos(Number(id)),
        prestacoesApi.getComentarios(Number(id)),
      ]);
      setPrestacao(prestacaoData);
      setDocumentos(documentosData);
      setComentarios(comentariosData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComentarioGeral = async () => {
    const comentarioTexto = novoComentarioGeral.trim();
    if (!comentarioTexto || !user || !id) return;

    try {
      setEnviandoComentarioGeral(true);
      await prestacoesApi.addComentario(Number(id), {
        comentario: comentarioTexto,
      });
      setNovoComentarioGeral('');
      await loadData();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Erro ao adicionar comentário');
    } finally {
      setEnviandoComentarioGeral(false);
    }
  };

  const handleAddComentario = async (documentoId: number) => {
    const comentarioTexto = novoComentario[documentoId]?.trim();
    if (!comentarioTexto || !user) return;

    try {
      setEnviandoComentario(documentoId);
      await prestacoesApi.addComentario(Number(id), {
        comentario: comentarioTexto,
        documento_id: documentoId,
      });
      setNovoComentario(prev => ({ ...prev, [documentoId]: '' }));
      await loadData();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Erro ao adicionar comentário');
    } finally {
      setEnviandoComentario(null);
    }
  };

  const handleValidarDocumento = async (documentoId: number, status: 'deferido' | 'indeferido', motivo?: string, glosa?: number, notificacao?: string) => {
    try {
      setValidandoDoc(documentoId);
      await documentosApi.validar(documentoId, status === 'deferido', motivo, glosa, notificacao);
      await loadData();
    } catch (error) {
      console.error('Error validating document:', error);
      alert('Erro ao validar documento');
    } finally {
      setValidandoDoc(null);
    }
  };

  const handleIndeferirClick = (docId: number) => {
    setModalIndeferimento({ docId });
    setMotivoIndeferimento('');
    setValorGlosa('');
    setNotificacaoMensagem('');
  };

  const handleConfirmarIndeferimento = async () => {
    if (!modalIndeferimento || !motivoIndeferimento.trim()) {
      alert('O motivo do indeferimento é obrigatório');
      return;
    }

    if (!notificacaoMensagem.trim()) {
      alert('A notificação para o administrador é obrigatória');
      return;
    }

    const glosa = valorGlosa ? parseFloat(valorGlosa) : undefined;
    await handleValidarDocumento(
      modalIndeferimento.docId,
      'indeferido',
      motivoIndeferimento,
      glosa,
      notificacaoMensagem
    );
    setModalIndeferimento(null);
  };

  const handleDownloadNF = async (documentoId: number, nfPath: string) => {
    try {
      const url = await filesApi.getDownloadUrl(nfPath);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading NF:', error);
      alert('Erro ao baixar nota fiscal');
    }
  };

  const handleEditDocument = async (documentoId: number) => {
    // `/prestacoes/${id}/documentos/${doc.id}/editar`
  }

  const getMesNome = (mes: number) => {
    const meses = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return meses[mes - 1] || '';
  };

  const getStatusBadgeStyle = (status: 'pendente' | 'deferido' | 'indeferido') => {
    switch(status)
    {
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
      case 'deferido':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'indeferido':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      default:
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
    }
  }

  const getStatusBadge = (status: 'pendente' | 'deferido' | 'indeferido') => {
    const icons = {
      pendente: Clock,
      deferido: CheckCircle,
      indeferido: XCircle,
      default: Clock,
    };
  
    const Icon = icons[status] || icons['default'];
    const style = getStatusBadgeStyle(status);
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${style}`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="capitalize">{status}</span>
      </span>
    );
  };

  const getTipoDocumentoBadgeStyle = (tipo: 'nota_fiscal' | 'comprovante_pagamento') => {
    switch(tipo)
    {
      case 'nota_fiscal':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'comprovante_pagamento':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
    }
  }

  const getTipoLancamentoLabel = (tipo: 'rateio' | 'contas') => {
    switch(tipo)
    {
      case 'rateio':
        return 'Rateio'
      case 'contas':
        return 'Contas a pagar'
      default:
        return 'Desconhecido'
    }
  }

  const getTipoDocumentoLabel = (tipo: 'nota_fiscal' | 'comprovante_pagamento') => {
    switch(tipo)
    {
      case 'nota_fiscal':
        return 'Nota Fiscal'
      case 'comprovante_pagamento':
        return 'Comprovante'
      default:
        return 'Desconhecido'
    }
  }

  const getTipoDocumentoBadge = (tipo: 'nota_fiscal' | 'comprovante_pagamento') => {
    const label = getTipoDocumentoLabel(tipo)
    const badgeStyle = getTipoDocumentoBadgeStyle(tipo);
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badgeStyle}`}>
        {label}
      </span>
    );
  };

  const documentosFiltrados = documentos.filter(doc => {
    if (!filtroBusca && !filtroStatus) return true;

    const statusMatch = !filtroStatus
      || doc.validacao_status.toLowerCase() == filtroStatus

    const textMatch = (
      doc.fornecedor_nome.toLowerCase().includes(filtroBusca) ||
      doc.fornecedor_documento.toLowerCase().includes(filtroBusca) ||
      doc.numero_documento.toLowerCase().includes(filtroBusca)
    )

    return statusMatch && textMatch;
  });

  const comentariosGerais = comentarios.filter(c => !c.documento_id);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!prestacao) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              Prestação não encontrada
            </h3>
            <div className="mt-6">
              <Link
                to="/prestacoes"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                Voltar para Prestações
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/prestacoes"
          className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar para Prestações</span>
        </Link>

        {/* Header da Prestação */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Prestação de Contas - {getMesNome(prestacao.mes)}/{prestacao.ano}
              </h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center space-x-2">
                  <Building2 className="w-4 h-4" />
                  <span>{prestacao.unidade.nome}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(prestacao.criado_em).toLocaleDateString('pt-BR')}</span>
                </div>
              </div>
            </div>
            <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              prestacao.status === 'aberto'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
            }`}>
              {prestacao.status === 'aberto' ? 'Aberto' : 'Fechado'}
            </span>
          </div>

          {/* Estatísticas */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {prestacao.total_documentos || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total de Documentos</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {prestacao.documentos_deferidos || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Deferidos</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                {prestacao.documentos_pendentes || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Pendentes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                {prestacao.documentos_indeferidos || 0}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Indeferidos</div>
            </div>
          </div>
        </div>

        {/* Sistema de Comentários Gerais */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <button
            onClick={() => setMostrarComentarios(!mostrarComentarios)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center space-x-3">
              <MessageSquare className="w-5 h-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Comentários e Notificações
              </h2>
              {comentariosGerais.length > 0 && (
                <span className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium rounded-full">
                  {comentariosGerais.length}
                </span>
              )}
            </div>
            {mostrarComentarios ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </button>

          {mostrarComentarios && (
            <div className="mt-4 space-y-4">
              {/* Lista de comentários gerais */}
              {comentariosGerais.length > 0 ? (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {comentariosGerais.map((comentario) => (
                    <div
                      key={comentario.id}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {comentario.criado_por?.nome || ''}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(comentario.criado_em).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {comentario.mensagem}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                  Nenhum comentário ainda
                </p>
              )}

              {/* Formulário para novo comentário */}
              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={novoComentarioGeral}
                    onChange={(e) => setNovoComentarioGeral(e.target.value)}
                    placeholder="Adicionar comentário ou notificação..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !enviandoComentarioGeral) {
                        handleAddComentarioGeral();
                      }
                    }}
                  />
                  <button
                    onClick={handleAddComentarioGeral}
                    disabled={!novoComentarioGeral.trim() || enviandoComentarioGeral}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Enviar</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Busca e Ações */}
        <div className="flex items-center justify-between mb-6">
          <div className='flex items-center gap-2'>

            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={filtroBusca}
                onChange={(e) => setFiltroBusca(e.target.value)}
                placeholder="Buscar por empresa, CNPJ ou número..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value as DocumentoFiscalStatus)}
                className="w-full min-h-[42px] px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value={""}>Todos status</option>
                {statusOptions.map(({ label, value }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>


          <Link
            to={`/prestacoes/${id}/documentos/novo`}
            className="ml-4 inline-flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            <FileText className="w-5 h-5" />
            <span>Adicionar Documento</span>
          </Link>
        </div>

        {/* Lista de Documentos */}
        <div className="space-y-4">
          {documentosFiltrados.map((doc) => {
            const comentariosDoc = comentarios.filter(c => c.documento_id === doc.id);
            const comentarioAberto = comentarioExpandido === doc.id;

            const buttonStyle = getDocumentButtonClass(doc.validacao_status.toLowerCase() as any)

            // CARD DOCUMENTO
            return (
              <div
                key={doc.id}
                className={[
                  'bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6',
                  getStatusBorderClass(doc.validacao_status.toLowerCase() as any),
                ].join(' ')}
              >
                <div className="flex items-start justify-between mb-4 gap-4">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-2 mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {doc.fornecedor_nome}
                      </h3>
                      {/*getTipoDocumentoBadge(doc.tipo_documento)*/}
                      {getStatusBadge(doc.validacao_status.toLowerCase() as any)}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">CNPJ:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-mono">
                          {doc.fornecedor_documento || ''}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Nº Documento:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {doc.numero_documento || ''}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Data Emissão:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {new Date(doc.data_emissao).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Rubrica:</span>
                        <span className="ml-2 text-primary-600 dark:text-primary-400 font-medium">
                          {doc.rubrica_documento}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Valor Original:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-semibold">
                          {formatCurrencyWithSymbol(doc.valor_original)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Valor Líquido:</span>
                        <span className="ml-2 text-gray-900 dark:text-white font-semibold">
                          {formatCurrencyWithSymbol(doc.valor_liquido)}
                        </span>
                      </div>

                      {doc.valor_glosa && parseFloat(doc.valor_glosa) > 0 && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Glosa:</span>
                          <span className="ml-2 text-red-600 dark:text-red-400 font-semibold">
                            {formatCurrencyWithSymbol(doc.valor_glosa)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                
                  {/* ✅ Ações do Documento: Ver NF / Ver Comprovante / Editar */}
                  <div className="flex flex-col items-end gap-2 min-w-[180px]">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        onClick={() => handleDownloadNF(doc.id, doc.caminho_arquivo_nota_fiscal!)}
                        disabled={!doc.caminho_arquivo_nota_fiscal}
                        className={["inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors", buttonStyle].join(' ')}
                      >
                        <FileText className="w-4 h-4" />
                        <span>Ver Nota Fiscal</span>
                        <span className={`ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full ${!doc.caminho_arquivo_nota_fiscal ? 'bg-black/5' : 'bg-white/20' } px-1 text-xs font-semibold`}>
                          {!!doc.caminho_arquivo_nota_fiscal ? 1 : 0}
                        </span>
                      </button>
    
                      <button
                        onClick={() => handleDownloadComprovante(doc.caminho_arquivo_comprovante)}
                        disabled={!doc.caminho_arquivo_comprovante}
                        className={["inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors", buttonStyle].join(' ')}
                      >
                        <Receipt className="w-4 h-4" />
                        <span>Ver Comprovante</span>
                        <span className={`ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full ${!doc.caminho_arquivo_comprovante ? 'bg-black/5' : 'bg-white/20' } px-1 text-xs font-semibold`}>
                          {!!doc.caminho_arquivo_comprovante ? 1 : 0}
                        </span>
                      </button>

                      {/*
                      <button
                        onClick={() => handleEditDocument(doc.id)}
                        className={["inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors", buttonStyle].join(' ')}
                      >
                        <Edit className="w-4 h-4" />
                        <span>Editar</span>
                      </button>
                      */}
                    </div>                    
                  </div>
                </div>

                {/* Motivo de Indeferimento */}
                {doc.validacao_status === 'indeferido' && doc.motivo_indeferimento && (
                  <div className="mb-4">
                    <button
                      onClick={() => setMotivoExpandido(motivoExpandido === doc.id ? null : doc.id)}
                      className="w-full flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-left"
                    >
                      <span className="text-sm font-medium text-red-800 dark:text-red-300">
                        Motivo do Indeferimento
                      </span>
                      {motivoExpandido === doc.id ? (
                        <ChevronUp className="w-4 h-4 text-red-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-red-600" />
                      )}
                    </button>
                    {motivoExpandido === doc.id && (
                      <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg">
                        <p className="text-sm text-red-800 dark:text-red-300">
                          {doc.motivo_indeferimento}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Comentários do Documento */}
                {comentariosDoc.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={() => setComentarioExpandido(comentarioAberto ? null : doc.id)}
                      className="w-full flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-left"
                    >
                      <div className="flex items-center space-x-2">
                        <MessageSquare className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          Comentários ({comentariosDoc.length})
                        </span>
                      </div>
                      {comentarioAberto ? (
                        <ChevronUp className="w-4 h-4 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    {comentarioAberto && (
                      <div className="mt-2 space-y-2 max-h-40 overflow-y-auto p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                        {comentariosDoc.map((comentario) => (
                          <div
                            key={comentario.id}
                            className="bg-white dark:bg-gray-700 rounded p-2 text-sm"
                          >
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-medium text-gray-900 dark:text-white text-xs">
                                {comentario.criado_por?.nome}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(comentario.criado_em).toLocaleString('pt-BR')}
                              </span>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300">
                              {comentario.mensagem}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Adicionar Comentário */}
                <div className="flex space-x-2 mb-4">
                  <input
                    type="text"
                    value={novoComentario[doc.id] || ''}
                    onChange={(e) => setNovoComentario(prev => ({ ...prev, [doc.id]: e.target.value }))}
                    placeholder="Adicionar comentário..."
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && enviandoComentario !== doc.id) {
                        handleAddComentario(doc.id);
                      }
                    }}
                  />
                  <button
                    onClick={() => handleAddComentario(doc.id)}
                    disabled={!novoComentario[doc.id]?.trim() || enviandoComentario === doc.id}
                    className="px-3 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>

                {/* Ações de Validação */}
                {doc.validacao_status.toLowerCase() === 'pendente' && (
                  <div className="mt-4 flex space-x-2 pt-4 border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleValidarDocumento(doc.id, 'deferido')}
                      disabled={validandoDoc === doc.id}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      <span>Deferir</span>
                    </button>
                    <button
                      onClick={() => handleIndeferirClick(doc.id)}
                      disabled={validandoDoc === doc.id}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>Indeferir</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {documentosFiltrados.length === 0 && (
            <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                Nenhum documento encontrado
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {filtroBusca ? 'Tente ajustar sua busca' : 'Adicione o primeiro documento desta prestação'}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Modal de Indeferimento */}
      {modalIndeferimento && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Indeferir Documento
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Preencha as informações obrigatórias
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Motivo do Indeferimento *
                </label>
                <textarea
                  value={motivoIndeferimento}
                  onChange={(e) => setMotivoIndeferimento(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Descreva o motivo do indeferimento..."
                  rows={3}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valor da Glosa (opcional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={valorGlosa}
                  onChange={(e) => setValorGlosa(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notificação para o Administrador *
                </label>
                <textarea
                  value={notificacaoMensagem}
                  onChange={(e) => setNotificacaoMensagem(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Mensagem que será enviada ao administrador sobre este indeferimento..."
                  rows={3}
                  required
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Esta mensagem será recebida pelo administrador como notificação
                </p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setModalIndeferimento(null)}
                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmarIndeferimento}
                disabled={!motivoIndeferimento.trim() || !notificacaoMensagem.trim()}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar Indeferimento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
